import { useQuery } from '@apollo/client';
import { ProgressIndicator, Stack, Text, makeStyles } from '@fluentui/react';
import { ToggleButton } from 'common/components/Buttons';
import { Chip } from 'common/components/Chip';
import { NoDataView } from 'common/components/DataPlaceholders';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { WorkgroupGroupValues } from '../types';

type workgroupGroupItems = CorporateWorkgroupsItems_corporateWorkgroups_nodes;

import { WorkgroupGroupRow } from 'settings/account/workgroupGroups/list';
import GROUP_WORKGROUP from './CorporateWorkgroupsItems.graphql';
import {
  CorporateWorkgroupsItems,
  CorporateWorkgroupsItems_corporateWorkgroups_nodes,
} from './__generated__/CorporateWorkgroupsItems';

const useStyles = makeStyles(() => ({
  container: {
    marginBottom: 200,
  },
  groupItems: {
    maxHeight: 250,
    overflowY: 'scroll',
    overflowX: 'hidden',
    padding: '0px 20px',
  },
  heading: {
    marginLeft: 20,
  },
  progressContainer: {
    padding: '0px 20px',
  },
}));

interface GroupItemsProps {
  workgroupGroup?: WorkgroupGroupRow;
}

export const GroupItems: React.FC<GroupItemsProps> = ({ workgroupGroup }) => {
  const styles = useStyles();
  const [groupItems, setGroupItems] = useState<workgroupGroupItems[]>([]);
  const [selectedGroupItems, setSelectedGroupItems] = useState<
    workgroupGroupItems[]
  >([]);

  const { id, workgroupGroupItems } = { ...workgroupGroup };

  const isEdit = !!id;

  const { control, setValue } = useFormContext<WorkgroupGroupValues>();

  const workgroupGroups = useWatch({
    name: 'workgroupGroupItems',
    control,
  });

  const { loading, data } = useQuery<CorporateWorkgroupsItems>(
    GROUP_WORKGROUP,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const onToggleClick = (workgroup: workgroupGroupItems) => {
    if (!!workgroup.id) {
      const isExists = workgroupGroups?.some(
        (item) => item.workgroupId === workgroup.id
      );
      if (!isExists) {
        setSelectedGroupItems([...selectedGroupItems, workgroup]);
        setValue(
          'workgroupGroupItems',
          [...(workgroupGroups || []), { workgroupId: workgroup.id }],
          { shouldDirty: true }
        );
        const filteredArray = groupItems.filter(
          (item) => item.id !== workgroup.id
        );
        setGroupItems(filteredArray);
      }
    }
  };

  const onRemoveClick = (workgroup: workgroupGroupItems) => {
    if (!!workgroup.id) {
      const isExists = selectedGroupItems?.some(
        (item) => item.id === workgroup.id
      );
      if (isExists) {
        const filteredArray = selectedGroupItems.filter(
          (item) => item.id !== workgroup.id
        );
        setSelectedGroupItems(filteredArray);
        setGroupItems([workgroup, ...groupItems]);
        setValue(
          'workgroupGroupItems',
          [
            ...filteredArray.map((ele) => ({
              workgroupId: ele.id,
            })),
          ],
          { shouldDirty: true }
        );
      }
    }
  };

  useEffect(() => {
    const { nodes } = { ...data?.corporateWorkgroups };
    const workgroupGroupArray: workgroupGroupItems[] =
      workgroupGroupItems?.nodes.map((item) => ({
        id: item.workgroupId,
        name: item.corporateWorkgroup?.name || '',
      })) || [];
    if (!!workgroupGroupArray.length) {
      setSelectedGroupItems(workgroupGroupArray);
      const filteredArray =
        nodes?.filter((item) =>
          workgroupGroupArray.every((ele) => ele.id !== item.id)
        ) || [];
      if (!!nodes?.length) {
        setGroupItems(filteredArray);
      }
    } else {
      if (!!nodes?.length) {
        setGroupItems(nodes);
      }
    }
  }, [workgroupGroupItems, data, isEdit]);

  return (
    <Stack
      className={styles.container}
      tokens={{
        childrenGap: 20,
      }}
    >
      <Text variant="large" className={styles.heading}>
        Available Workgroups
      </Text>
      {loading ? (
        <Stack className={styles.progressContainer}>
          <ProgressIndicator />
        </Stack>
      ) : (
        <Stack
          wrap
          horizontal
          tokens={{
            childrenGap: 10,
          }}
          className={styles.groupItems}
        >
          {groupItems?.map((item, index) => {
            return (
              <Stack key={index}>
                <ToggleButton
                  onClick={() => onToggleClick(item)}
                  key={item.id}
                  text={item.name}
                  iconName={'Add'}
                />
              </Stack>
            );
          })}
          <Stack grow>
            <NoDataView
              show={!groupItems?.length}
              title="No records available."
            />
          </Stack>
        </Stack>
      )}
      <Text variant="large" className={styles.heading}>
        Selected Workgroups
      </Text>
      <Stack
        wrap
        horizontal
        tokens={{
          childrenGap: 10,
        }}
        className={styles.groupItems}
      >
        {selectedGroupItems?.map((item, index) => {
          return (
            <Stack key={index}>
              <Chip
                // disabled={loadingRemoveTag || isDirty}
                text={item.name}
                onClick={() => onRemoveClick(item)}
                color={'RED'}
              />
            </Stack>
          );
        })}
        <Stack grow>
          <NoDataView
            show={!selectedGroupItems?.length}
            title="No records available"
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
